import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight } from "lucide-react";
import DOMPurify from "dompurify";
import { fetchPost, formatDate } from "@/lib/blog";
import { authorForBlog } from "@/lib/authors";
import AuthorBadge from "@/components/blog/AuthorBadge";
import ShareButtons from "@/components/blog/ShareButtons";
import { useSeo, SITE_URL } from "@/lib/seo";

const articleJsonLd = (post, author) => ({
  "@context": "https://schema.org",
  "@type": "BlogPosting",
  headline: post.title,
  description: post.excerpt,
  image: post.image || undefined,
  datePublished: post.published_at,
  dateModified: post.updated_at,
  articleSection: post.category,
  mainEntityOfPage: `${SITE_URL}/blog/${post.slug}`,
  author: { "@type": "Person", name: author.name, jobTitle: author.role, sameAs: author.linkedin },
  publisher: { "@id": `${SITE_URL}/#organization` },
});

export default function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(undefined);

  useEffect(() => {
    setPost(undefined);
    fetchPost(slug).then(setPost).catch(() => setPost(null));
  }, [slug]);

  const author = post ? authorForBlog(post) : null;

  useSeo({
    title: post ? `${post.title} | Blog Idea 360` : "Blog | Idea 360 Consulting",
    description: post?.excerpt || "Articolo del blog di Idea 360 Consulting.",
    path: `/blog/${slug}`,
    jsonLd: post ? articleJsonLd(post, author) : null,
  });

  if (post === undefined) {
    return <div className="max-w-3xl mx-auto px-6 py-32 text-white/50" data-testid="blogpost-loading">Caricamento…</div>;
  }
  if (post === null) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-32 text-center" data-testid="blogpost-notfound">
        <h1 className="text-3xl font-bold text-white mb-6" style={{ fontFamily: "'Outfit', sans-serif" }}>Articolo non trovato</h1>
        <Link to="/blog" className="btn-outline-yellow inline-flex items-center gap-2">
          <ArrowLeft size={16} /> Torna al blog
        </Link>
      </div>
    );
  }

  return (
    <>
      <article className="max-w-3xl mx-auto px-6 lg:px-8 pt-16 sm:pt-24 pb-20" data-testid="blogpost-article">
        <Link to="/blog" className="inline-flex items-center gap-2 text-sm text-white/60 hover:text-[#FACC15] transition-colors" data-testid="blogpost-back">
          <ArrowLeft size={15} /> Blog
        </Link>
        <p className="eyebrow mt-8 mb-4" data-testid="blogpost-category">{post.category}</p>
        <h1
          className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-[1.05]"
          style={{ fontFamily: "'Outfit', sans-serif", letterSpacing: "-0.03em" }}
          data-testid="blogpost-h1"
        >
          {post.title}
        </h1>
        <time className="block mt-6 text-white/45 text-sm" dateTime={post.published_at} data-testid="blogpost-date">
          {formatDate(post.published_at)}
        </time>
        <div className="mt-8 flex flex-col sm:flex-row sm:items-center justify-between gap-6 border-y border-[#1a1a1a] py-5">
          <AuthorBadge author={author} className="sm:min-w-[320px]" />
          <ShareButtons path={`/blog/${post.slug}`} title={post.title} />
        </div>
        {post.image && (
          <img src={post.image} alt={post.title} className="mt-10 w-full aspect-[16/9] object-cover border border-[#1a1a1a]" data-testid="blogpost-image" />
        )}
        <div
          className="blog-content mt-12"
          data-testid="blogpost-content"
          dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(post.content) }}
        />
        <div className="mt-12 pt-8 border-t border-[#1a1a1a]">
          <ShareButtons path={`/blog/${post.slug}`} title={post.title} />
        </div>
      </article>

      <section className="py-20 border-t border-[#141414]">
        <div className="max-w-3xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-white mb-6" style={{ fontFamily: "'Outfit', sans-serif", letterSpacing: "-0.025em" }}>
            Vuoi applicarlo alla tua azienda?
          </h2>
          <Link to="/contatti" className="btn-yellow inline-flex items-center gap-2" data-testid="blogpost-cta">
            Richiedi una consulenza <ArrowRight size={18} />
          </Link>
        </div>
      </section>
    </>
  );
}
